import { Link } from "react-router-dom";
import { Lock, ArrowRight } from "lucide-react";
import { useInView } from "@/components/living/useScrollStage";

const COLS = ["Input", "System", "Action"];

/**
 * Four phases. The outcome of each phase is shown; the exact input/system/action
 * build is blurred out and unlocked on the call. Showing the destination but not
 * the turn-by-turn directions is what makes the call worth booking.
 */
export default function Roadmap({ phases }) {
  const { ref, on } = useInView(0.2);
  return (
    <>
      <ol ref={ref} className="border-t border-l border-ink" data-testid="roadmap">
        {phases.map((p, i) => (
          <li key={p.n} className="stagerow grid grid-cols-1 md:grid-cols-12 border-r border-b border-ink" data-on={on} style={{ transitionDelay: on ? `${i * 0.15}s` : "0s" }}>
            <div className="md:col-span-3 p-5 border-b md:border-b-0 md:border-r border-ink flex md:block items-baseline gap-3">
              <span className="numeral text-[40px] md:text-[52px] leading-[0.85]">{p.n}</span>
              <span className="eyebrow block md:mt-3">Phase</span>
              <h3 className="display text-[22px] md:text-[26px] leading-[1.1] md:mt-2">{p.title}</h3>
            </div>
            <div className="md:col-span-6 relative grid grid-cols-3 border-b md:border-b-0 md:border-r border-ink">
              {[p.input, p.system, p.action].map((cell, j) => (
                <div key={COLS[j]} className="p-4 border-r border-ink/25 last:border-r-0">
                  <span className="eyebrow block mb-2">{COLS[j]}</span>
                  <p className="text-[14px] leading-snug text-ink/85 blur-[5px] select-none" aria-hidden="true">{cell}</p>
                </div>
              ))}
              <span className="sr-only">Exact input, system and action for this phase are shown on your call.</span>
              <div className="absolute inset-0 flex items-center justify-center gap-2 bg-paper/60">
                <Lock size={13} className="text-ink/50" aria-hidden="true" />
                <span className="text-[11px] uppercase tracking-[0.08em] text-ink/60">Shown on your call</span>
              </div>
            </div>
            <div className="md:col-span-3 p-4">
              <span className="eyebrow block mb-2">Outcome</span>
              <p className="text-[14px] leading-snug font-medium text-cobalt">{p.outcome}</p>
            </div>
          </li>
        ))}
      </ol>
      <div className="mt-6 border border-ink p-5 md:p-6 flex flex-wrap items-center justify-between gap-4">
        <p className="text-[15px] text-ink/75 max-w-[46ch]">
          The exact systems and steps for each phase are built around your workflow,
          not a template. We walk through all four, live, on the call.
        </p>
        <Link to="/book" className="pill pill-cobalt" data-testid="roadmap-book">
          See the full plan <ArrowRight size={16} />
        </Link>
      </div>
    </>
  );
}
